import { useState } from 'react';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import { dataCollectionAPI } from '@vehix/portal/services/api/dataCollection';
import {
  isCompletedStatus,
  isFailedStatus,
  isRunningStatus,
  normalizeStatus
} from '@vehix/portal/utils/executionStatus';

/**
 * Hook to manually sync a specific Apify run's status
 */
export function useManualApifySync() {
  const [syncing, setSyncing] = useState(false);
  const { toast } = useToast();

  const syncSpecificRun = async (runId: string) => {
    setSyncing(true);

    try {
      // Fetch authoritative execution status from backend (auto-syncs when stale)
      const execution = await dataCollectionAPI.getExecution(runId);

      if (!execution) {
        toast({
          title: 'Run Not Found',
          description: `Could not find execution with ID: ${runId}`,
          variant: 'destructive'
        });
        return false;
      }

      const normalizedStatus = normalizeStatus(execution.status);

      // If the run completed successfully, trigger backfill to process the dataset
      if (isCompletedStatus(normalizedStatus)) {
        toast({
          title: 'Processing Dataset',
          description: `Fetching and processing data for run ${runId}...`
        });

        try {
          // Trigger backfill to process the Apify dataset
          const backfillResult = await dataCollectionAPI.backfillRun(runId, true);

          toast({
            title: 'Dataset Processed',
            description: `Run ${runId} completed with ${backfillResult.result.records || 0} records processed`,
            variant: 'default'
          });
        } catch (backfillError) {
          console.error('Backfill failed:', backfillError);
          toast({
            title: 'Dataset Processing Failed',
            description: `Status updated but failed to process dataset: ${backfillError instanceof Error ? backfillError.message : 'Unknown error'}`,
            variant: 'destructive'
          });
          // Still return true because status was updated
        }
      } else if (isFailedStatus(normalizedStatus)) {
        toast({
          title: 'Task Failed',
          description: `Run ${runId} failed`,
          variant: 'destructive'
        });
      } else if (isRunningStatus(normalizedStatus)) {
        toast({
          title: 'Status Synced',
          description: `Run ${runId} is still running`,
          variant: 'default'
        });
      } else {
        toast({
          title: 'Status Synced',
          description: `Run ${runId} updated to ${normalizedStatus || 'unknown'}`,
          variant: 'default'
        });
      }

      return true;
    } catch (error) {
      console.error(`Failed to sync run ${runId}:`, error);
      toast({
        title: 'Sync Failed',
        description: error instanceof Error ? error.message : 'Failed to sync run status',
        variant: 'destructive'
      });
      return false;
    } finally {
      setSyncing(false);
    }
  };

  return {
    syncSpecificRun,
    syncing
  };
}
